/**
 * D-EZ-95 — THE SEASON'S AWARDS AND THE HALL OF FAME.
 *
 * Every award is read off something the league already recorded: the playoff
 * (Champion), the winter's seasons-run counter and the registry (Rookie of the
 * Year), Sagliano's royalty factors (Most Popular — SAG/27's fan-appeal and
 * media points, which is precisely "who the public took to"), two seasons'
 * ranks (Most Improved), and the plain counters. Nothing is voted on, so
 * nothing can be invented.
 */

import type { DriverRegistry, RegistryEntry } from './registry.ts';
import { sortedSeasons, winningsBy, type HistorySeason } from './source.ts';

export type AwardKind =
  | 'champion'
  | 'rookie-of-the-year'
  | 'most-popular'
  | 'most-improved'
  | 'most-wins'
  | 'pole-award'
  | 'most-turns-led'
  | 'money-leader'
  | 'iron-man'
  | 'hard-charger';

export interface Award {
  kind: AwardKind;
  /** Plain-language title, ready to print. */
  title: string;
  driverId: string;
  name: string;
  /** The number behind it, with its unit, e.g. "7 wins", "$2.4M". */
  value: number;
  basis: string;
}

const TITLES: Record<AwardKind, string> = {
  champion: 'Champion',
  'rookie-of-the-year': 'Rookie of the Year',
  'most-popular': 'Most Popular Driver',
  'most-improved': 'Most Improved',
  'most-wins': 'Most Wins',
  'pole-award': 'Pole Award',
  'most-turns-led': 'Most Miles Led',
  'money-leader': 'Money Leader',
  'iron-man': 'Iron Man',
  'hard-charger': 'Hard Charger',
};

/**
 * The awards for one season. A season still being raced gets the counting
 * awards so far and no Champion. Returns [] for a season number the league has
 * not run. `registry` decides who was a rookie: a driver whose first league
 * season is this one, in any season after the league's first (the founding
 * field are not rookies — they are the league).
 */
export function seasonAwards(
  seasons: readonly HistorySeason[],
  seasonNumber: number,
  registry?: DriverRegistry,
): Award[] {
  const ordered = sortedSeasons(seasons);
  const s = ordered.find((x) => x.seasonNumber === seasonNumber);
  if (!s) return [];
  const standings = s.season.finalStandings;
  const drivers = standings.drivers.filter((d) => d.starts > 0);
  if (drivers.length === 0) return [];
  const out: Award[] = [];
  const push = (kind: AwardKind, driverId: string, name: string, value: number, basis: string): void => {
    out.push({ kind, title: TITLES[kind], driverId, name, value, basis });
  };
  const best = <T>(xs: readonly T[], score: (x: T) => number, tie: (a: T, b: T) => number): T | null => {
    let top: T | null = null;
    for (const x of xs) {
      if (top === null || score(x) > score(top) || (score(x) === score(top) && tie(x, top) < 0)) top = x;
    }
    return top;
  };
  const byRank = (a: { rank: number }, b: { rank: number }): number => a.rank - b.rank;

  if (s.season.championId && s.complete !== false) {
    const c = standings.byDriver[s.season.championId];
    if (c) push('champion', c.driverId, c.name, c.points, `won the title with ${c.wins} win${c.wins === 1 ? '' : 's'} and ${c.points} points`);
  }

  // Rookie of the Year.
  const firstLeagueSeason = ordered[0]?.seasonNumber ?? seasonNumber;
  if (registry && seasonNumber > firstLeagueSeason) {
    const rookies = drivers.filter((d) => registry.byId[d.driverId]?.firstSeason === seasonNumber);
    const roty = best(rookies, (d) => -d.rank, byRank);
    if (roty) push('rookie-of-the-year', roty.driverId, roty.name, roty.rank, `best-placed first-year driver — ${ordinal(roty.rank)} in the standings`);
  }

  // Most Popular — SAG/27's fan appeal + media presence, the public's half of
  // the royalty formula. Only a settled season has royalties.
  const royalties = s.finance?.royalties ?? [];
  if (royalties.length > 0) {
    const pop = best(
      royalties,
      (r) => r.fanPoints + r.mediaPoints,
      (a, b) => b.tvAppearances - a.tvAppearances || a.driverId.localeCompare(b.driverId),
    );
    if (pop && pop.fanPoints + pop.mediaPoints > 0) {
      push('most-popular', pop.driverId, pop.name, pop.tvAppearances, `the fans' and cameras' favourite — ${pop.tvAppearances} TV weeks, fan appeal ${pop.fanPoints >= 0 ? '+' : ''}${pop.fanPoints}`);
    }
  }

  // Most Improved — the biggest climb in the championship on last year.
  const prev = ordered.find((x) => x.seasonNumber === seasonNumber - 1);
  if (prev) {
    const climbers = drivers
      .map((d) => ({ d, before: prev.season.finalStandings.byDriver[d.driverId] }))
      .filter((x) => x.before && x.before.starts > 0)
      .map((x) => ({ ...x.d, gain: (x.before as { rank: number }).rank - x.d.rank }));
    const mi = best(climbers, (x) => x.gain, byRank);
    if (mi && mi.gain > 0) push('most-improved', mi.driverId, mi.name, mi.gain, `up ${mi.gain} places to ${ordinal(mi.rank)}`);
  }

  const wins = best(drivers, (d) => d.wins, byRank);
  if (wins && wins.wins > 0) push('most-wins', wins.driverId, wins.name, wins.wins, `${wins.wins} win${wins.wins === 1 ? '' : 's'}`);
  const poles = best(drivers, (d) => d.poles, byRank);
  if (poles && poles.poles > 0) push('pole-award', poles.driverId, poles.name, poles.poles, `${poles.poles} pole${poles.poles === 1 ? '' : 's'}`);
  const led = best(drivers, (d) => d.turnsLed, byRank);
  if (led && led.turnsLed > 0) push('most-turns-led', led.driverId, led.name, led.turnsLed * 10, `${led.turnsLed * 10} miles out front`);
  const money = winningsBy(s.finance?.events);
  const rich = best(drivers, (d) => money.get(d.driverId) ?? 0, byRank);
  if (rich && (money.get(rich.driverId) ?? 0) > 0) {
    push('money-leader', rich.driverId, rich.name, money.get(rich.driverId) ?? 0, 'most won on the racetrack this season');
  }
  const iron = best(drivers, (d) => d.racesFinished * 1000 - d.dnfs, byRank);
  if (iron) push('iron-man', iron.driverId, iron.name, iron.racesFinished, `${iron.racesFinished} races running at the finish`);
  const charger = best(
    drivers.filter((d) => d.starts >= Math.max(5, Math.ceil(s.season.races.length / 2)) && d.avgStart !== null && d.avgFinish !== null),
    (d) => (d.avgStart as number) - (d.avgFinish as number),
    byRank,
  );
  if (charger && (charger.avgStart as number) > (charger.avgFinish as number)) {
    const gain = (charger.avgStart as number) - (charger.avgFinish as number);
    push('hard-charger', charger.driverId, charger.name, Math.round(gain * 10) / 10, `gained ${gain.toFixed(1)} places a race on average`);
  }
  return out;
}

// ---------------------------------------------------------------------------
// The Hall of Fame
// ---------------------------------------------------------------------------

/**
 * D-EZ-95 — INDUCTION, written down so it can be argued with.
 *
 * Eligible once his career is over (retired, or otherwise no longer licensed —
 * a death in the car is eligible at once). Inducted the season after he
 * leaves, on ANY of:
 *
 *   • two or more championships;
 *   • one championship and 15 or more wins;
 *   • 30 or more wins;
 *   • 15 or more wins and 300 or more starts (a long, winning career);
 *
 * — counting pre-league priors where a scenario supplied them (D-EZ-97). A
 * season is 36 races and a dominant season is six to eleven wins, so these
 * ask for roughly a decade at or near the top.
 */
export const HALL_OF_FAME_RULES = {
  titles: 2,
  titlePlusWins: { titles: 1, wins: 15 },
  wins: 30,
  longCareer: { wins: 15, starts: 300 },
} as const;

export interface HallOfFamer {
  driverId: string;
  name: string;
  /** The season he was inducted in (the season after his last). */
  inductedSeason: number;
  lastSeason: number;
  championships: number;
  wins: number;
  starts: number;
  winningsMb: number;
  /** Which bar he cleared, in words. */
  citation: string;
}

export function hallOfFameCase(e: RegistryEntry): string | null {
  const c = e.career;
  const r = HALL_OF_FAME_RULES;
  if (c.championships >= r.titles) return `${c.championships} championships`;
  if (c.championships >= r.titlePlusWins.titles && c.wins >= r.titlePlusWins.wins)
    return `a champion with ${c.wins} career wins`;
  if (c.wins >= r.wins) return `${c.wins} career wins`;
  if (c.wins >= r.longCareer.wins && c.starts >= r.longCareer.starts)
    return `${c.wins} wins across ${c.starts} starts`;
  return null;
}

/**
 * Everyone inducted so far, earliest class first. `throughSeason` is the
 * current season: a driver who retired after season 6 is inducted in season 7,
 * and is not in the Hall while season 7 has not begun.
 */
export function hallOfFame(registry: DriverRegistry, throughSeason: number): HallOfFamer[] {
  const out: HallOfFamer[] = [];
  for (const e of registry.former) {
    const inductedSeason = e.status === 'died' ? e.lastSeason : e.lastSeason + 1;
    if (inductedSeason > throughSeason) continue;
    const citation = hallOfFameCase(e);
    if (!citation) continue;
    out.push({
      driverId: e.driverId,
      name: e.name,
      inductedSeason,
      lastSeason: e.lastSeason,
      championships: e.career.championships,
      wins: e.career.wins,
      starts: e.career.starts,
      winningsMb: e.career.winningsMb,
      citation,
    });
  }
  return out.sort((a, b) => a.inductedSeason - b.inductedSeason || b.wins - a.wins || a.name.localeCompare(b.name));
}

function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] ?? s[v] ?? s[0]);
}
